import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Stack,
  Button,
  useTheme,
  alpha,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Payment,
  NotificationsNone as NotificationsIcon,
  AccountCircle,
  Logout,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import AppLogo from "../components/brand/AppLogo";
import PoweredByBrand from "../components/brand/PoweredByBrand";
import { BRAND } from "../constants/brand";
import { formatRoleLabel } from "../constants/roleLabels";
import HeaderSearch from "../components/layout/HeaderSearch";
import { useAuth } from "../contexts/AuthContext";
import { useAuthStore } from "../stores/authStore";
import { useRBAC } from "../hooks/useRBAC";
import financialYearAPI, {
  formatFinancialYearDisplay,
  indiaFyMeta,
} from "../services/financialYearAPI";
import { pageBackground, tokens } from "../theme";
import {
  buildAdminNavSections,
  filterAdminNavSections,
  flattenAdminNavLeaves,
  groupsContainingPath,
  type AdminNavLeaf,
} from "../config/adminNav";
/** DreamsPOS shell: flat header + flat sidebar (no radius on chrome). */
const DashboardLayout: React.FC = () => {
  const theme = useTheme();
  const drawerWidth = theme.tokens.drawerWidth;
  const headerHeight = theme.tokens.shellHeaderHeight;
  const sidebar = theme.tokens.colors.sidebar;

  const headerBarSx = {
    height: headerHeight,
    minHeight: headerHeight,
    maxHeight: headerHeight,
    boxSizing: "border-box" as const,
  };

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { user } = useAuthStore();
  const { canRead, hasAnyRole, canPerformRecharge } = useRBAC();
  const isFieldStaff = hasAnyRole("OPERATOR", "MERCHANT", "SALES_POINT_ADMIN");
  const showQuickRecharge = canPerformRecharge();
  const [fyDisplay, setFyDisplay] = useState(() => indiaFyMeta().display);

  const isPosFullscreen = location.pathname === "/sales";

  const shouldHideSidebar = isPosFullscreen;

  const roleUpper = String(user?.role || "").toUpperCase();
  const isPlatformAdmin = roleUpper === "PLATFORM_ADMIN";
  const isSuperAdmin =
    roleUpper === "SUPER_ADMIN" ||
    roleUpper === "SUPERADMIN" ||
    isPlatformAdmin;

  const isBranchAdmin = roleUpper === "ADMIN";
  const isAdminOrAbove = isSuperAdmin || isPlatformAdmin || isBranchAdmin;

  const businessType =
    user?.company?.businessType ||
    localStorage.getItem("business_type") ||
    "ALL_IN_ONE";

  const navSections = useMemo(
    () =>
      buildAdminNavSections({
        isSuperAdmin,
        isPlatformAdmin,
        hasAnyRole,
        businessType,
      }),
    [isSuperAdmin, isPlatformAdmin, hasAnyRole, businessType]
  );

  const visibleSections = useMemo(
    () =>
      !user
        ? []
        : filterAdminNavSections(navSections, {
            isSuperAdmin,
            isPlatformAdmin,
            isAdminOrAbove,
            isFieldStaff,
            showQuickRecharge,
            canRead,
          }),
    [
      user,
      navSections,
      isSuperAdmin,
      isPlatformAdmin,
      isAdminOrAbove,
      isFieldStaff,
      showQuickRecharge,
      canRead,
    ]
  );

  const visibleMenuItems = useMemo(
    () => flattenAdminNavLeaves(visibleSections),
    [visibleSections]
  );

  useEffect(() => {
    const openIds = groupsContainingPath(visibleSections, location.pathname);
    if (!openIds.length) return;
    setExpandedGroups((prev) => {
      const next = { ...prev };
      for (const id of openIds) next[id] = true;
      return next;
    });
  }, [location.pathname, visibleSections]);

  useEffect(() => {
    let cancelled = false;
    financialYearAPI
      .current()
      .then((fy) => {
        if (!cancelled && fy) setFyDisplay(formatFinancialYearDisplay(fy));
      })
      .catch(() => {
        /* keep local india FY fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };
  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  /** Longest matching menu path wins (e.g. /reports/sales not /reports). */
  const isActive = (path: string) => {
    const pathname = location.pathname;
    const matches = visibleMenuItems
      .map((item) => item.path)
      .filter((p) => pathname === p || pathname.startsWith(`${p}/`));
    if (matches.length === 0) return false;
    const best = matches.reduce((a, b) => (a.length >= b.length ? a : b));
    return best === path;
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const leafButtonSx = (active: boolean) => ({
    borderRadius: 0,
    color: active ? sidebar.textActive : sidebar.text,
    py: 0.65,
    px: 1.25,
    minHeight: 32,
    borderLeft: active
      ? `2px solid ${theme.palette.primary.main}`
      : "2px solid transparent",
    bgcolor: active ? sidebar.bgActive : "transparent",
    "&.Mui-selected": {
      bgcolor: sidebar.bgActive,
      color: sidebar.textActive,
      "&:hover": { bgcolor: sidebar.bgActive },
      "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
    },
    "&:hover": {
      bgcolor: sidebar.bgHover,
      color: sidebar.textActive,
    },
  });

  const renderLeaf = (item: AdminNavLeaf, nested = false) => {
    const active = isActive(item.path);
    return (
      <ListItem key={item.path} disablePadding sx={nested ? { pl: 1.5 } : undefined}>
        <ListItemButton
          selected={active}
          onClick={() => handleNavigation(item.path)}
          sx={{
            ...leafButtonSx(active),
            ...(nested ? { pl: 2.5 } : {}),
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: nested ? 24 : 28,
              color: active ? theme.palette.primary.main : sidebar.text,
              "& svg": { fontSize: nested ? 16 : 18 },
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: nested ? "0.875rem" : "0.9375rem",
              fontWeight: active ? 600 : 500,
              lineHeight: 1.25,
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: sidebar.bg }}>
      <Box
        sx={{
          ...headerBarSx,
          px: 1.25,
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          borderBottom: `1px solid ${sidebar.border}`,
        }}
      >
        <AppLogo
          size="sm"
          showText
          title={BRAND.productNameAdmin}
          sx={{
            gap: 0.75,
            "& .MuiTypography-subtitle2": {
              color: sidebar.textActive,
              fontSize: "0.875rem",
              fontWeight: 650,
              lineHeight: 1.2,
            },
            "& .MuiTypography-caption": {
              color: sidebar.text,
              fontSize: "0.6875rem",
              lineHeight: 1.2,
            },
          }}
        />
      </Box>

      <List sx={{ flex: 1, py: 0, px: 0, overflow: "auto" }}>
        {visibleSections.map((section) => (
          <Box key={section.id}>
            <Typography
              variant="overline"
              sx={{
                px: 1.25,
                py: 0.75,
                display: "block",
                color: sidebar.section,
                fontSize: "0.75rem",
                letterSpacing: "0.05em",
                bgcolor: sidebar.bgHover,
                borderBottom: `1px solid ${sidebar.border}`,
              }}
            >
              {section.label}
            </Typography>
            {section.entries.map((entry) => {
              if (entry.kind === "leaf") {
                return renderLeaf(entry);
              }

              const expanded = expandedGroups[entry.id] ?? false;
              const groupActive = entry.children.some((c) => isActive(c.path));

              return (
                <React.Fragment key={entry.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => toggleGroup(entry.id)}
                      sx={{
                        ...leafButtonSx(groupActive),
                        pr: 0.75,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 28,
                          color: groupActive
                            ? theme.palette.primary.main
                            : sidebar.text,
                          "& svg": { fontSize: 18 },
                        }}
                      >
                        {entry.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={entry.text}
                        primaryTypographyProps={{
                          fontSize: "0.9375rem",
                          fontWeight: groupActive ? 600 : 500,
                          lineHeight: 1.25,
                        }}
                      />
                      {expanded ? (
                        <ExpandLess sx={{ fontSize: 18, color: sidebar.text }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 18, color: sidebar.text }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {entry.children.map((child) => renderLeaf(child, true))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </Box>
        ))}
      </List>

      {user && (
        <Box
          sx={{
            px: 1.25,
            py: 1,
            borderTop: `1px solid ${sidebar.border}`,
            bgcolor: sidebar.bg,
          }}
        >
          <Typography variant="caption" sx={{ color: sidebar.text, display: "block", mb: 0.15, fontSize: "0.6875rem" }}>
            Signed in as
          </Typography>
          <Typography variant="body2" sx={{ color: sidebar.textActive, fontWeight: 600, fontSize: "0.8125rem" }} noWrap>
            {user.name}
          </Typography>
          <Chip
            label={formatRoleLabel(user.role)}
            size="small"
            sx={{
              mt: 0.75,
              height: 22,
              fontSize: "0.7125rem",
              fontWeight: 600,
              borderRadius: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.dark,
            }}
          />
          <PoweredByBrand
            sx={{
              mt: 1,
              fontSize: "0.6625rem",
              color: sidebar.text,
              textAlign: "left",
            }}
          />
        </Box>
      )}
      {!user && (
        <Box sx={{ px: 1.25, py: 1, borderTop: `1px solid ${sidebar.border}` }}>
          <PoweredByBrand sx={{ fontSize: "0.6625rem", color: sidebar.text, textAlign: "left" }} />
        </Box>
      )}
    </Box>
  );

  const drawerPaperSx = {
    boxSizing: "border-box" as const,
    width: drawerWidth,
    bgcolor: sidebar.bg,
    borderRight: `1px solid ${sidebar.border}`,
    borderRadius: 0,
    boxShadow: "none",
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: pageBackground() }}>
      {!isPosFullscreen && (
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: shouldHideSidebar ? "100%" : { sm: `calc(100% - ${drawerWidth}px)` },
          ml: shouldHideSidebar ? 0 : { sm: `${drawerWidth}px` },
          bgcolor: "background.paper",
          color: "text.primary",
          borderRadius: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
          ...headerBarSx,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            ...headerBarSx,
            width: "100%",
            display: "flex",
            alignItems: "center",
            px: { xs: 1, sm: 1.25 },
            borderRadius: 0,
          }}
        >
          {/* Left: menu + search */}
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {!shouldHideSidebar && (
              <>
                <IconButton
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ display: { sm: "none" }, color: "text.primary", borderRadius: 0 }}
                >
                  <MenuIcon />
                </IconButton>
                <Box sx={{ display: { sm: "none" } }}>
                  <AppLogo size="sm" />
                </Box>
              </>
            )}

            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <HeaderSearch
                pages={visibleMenuItems.map(({ text, path, module }) => ({
                  text,
                  path,
                  module,
                }))}
                canRead={canRead}
                branchId={user?.branchId}
              />
            </Box>

            <Typography
              variant="subtitle1"
              noWrap
              sx={{ fontWeight: 650, fontSize: "0.875rem", display: { md: "none" }, maxWidth: 140 }}
            >
              {visibleMenuItems.find((i) => isActive(i.path))?.text || "Dashboard"}
            </Typography>
          </Stack>

          {/* Spacer pushes right cluster to the end */}
          <Box sx={{ flex: 1, minWidth: 8 }} />

          {/* Right: FY + notifications + profile */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flexShrink: 0, ml: "auto" }}
          >
            <Chip
              label={fyDisplay}
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: "0.7125rem",
                fontWeight: 700,
                borderRadius: 0,
                letterSpacing: "0.02em",
              }}
            />
            {showQuickRecharge && (
              <Button
                size="small"
                variant="contained"
                startIcon={<Payment />}
                onClick={() => navigate("/quick-recharge")}
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  boxShadow: "none",
                  fontWeight: 700,
                  mr: 0.5,
                }}
              >
                Quick Recharge
              </Button>
            )}
            <Tooltip title="Inventory Alerts & Low Stock">
              <IconButton
                size="small"
                onClick={() => navigate("/reports/inventory-alerts")}
                sx={{ color: "text.secondary", borderRadius: 0 }}
              >
                <NotificationsIcon />
              </IconButton>
            </Tooltip>

            {user && (
              <>
                <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "right", mr: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} lineHeight={1.15} noWrap sx={{ fontSize: "0.8125rem" }}>
                    {user.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.6875rem" }}>
                    {user.role}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  sx={{ borderRadius: tokens.radius.sm, p: 0.25 }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: "primary.main",
                      fontSize: "0.7625rem",
                      fontWeight: 650,
                    }}
                  >
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </Avatar>
                </IconButton>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>
      )}

      {!shouldHideSidebar && (
        <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": drawerPaperSx,
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": drawerPaperSx,
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: shouldHideSidebar ? "100%" : { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          bgcolor: pageBackground(),
        }}
      >
        {!isPosFullscreen && <Toolbar sx={headerBarSx} />}
        <Box
          sx={{
            px: isPosFullscreen ? 0 : { xs: 1, sm: 1.25 },
            py: isPosFullscreen ? 0 : 1,
            pb: isPosFullscreen ? 0 : 1.5,
          }}
        >
          {!user && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              Please log in to access the dashboard.
            </Typography>
          )}
          <Outlet />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 180,
              mt: 0.5,
              borderRadius: tokens.radius.sm,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleNavigation("/profile");
            handleProfileMenuClose();
          }}
        >
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardLayout;
